import { useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<AuthUser>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const DEMO_USER: AuthUser = {
  id: 'demo-user',
  email: 'demo@nobelcapital.com.br',
  name: 'Rafael Brandão',
  role: 'admin',
};

export function useAuth(): AuthState {
  const isDemoMode = typeof window !== 'undefined' && sessionStorage.getItem('demo') === '1';
  const qc = useQueryClient();

  const [user, setUser] = useState<AuthUser | null>(isDemoMode ? DEMO_USER : null);
  const [isLoading, setIsLoading] = useState(!isDemoMode);

  const refresh = useCallback(async () => {
    if (isDemoMode) {
      setUser(DEMO_USER);
      setIsLoading(false);
      return;
    }
    try {
      const me = await api.get<AuthUser>('/api/auth/me');
      setUser(me);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
      } else {
        // Falha de rede: mantém sessão atual para não deslogar sem motivo
        console.error('[useAuth] refresh failed', err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isDemoMode]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const res = await api.post<{ user: AuthUser }>('/api/auth/login', { email, password });
      setUser(res.user);
      return res.user;
    },
    [],
  );

  const signOut = useCallback(async () => {
    if (isDemoMode) {
      sessionStorage.removeItem('demo');
    } else {
      try {
        await api.post('/api/auth/logout');
      } catch {
        // best effort
      }
    }
    setUser(null);
    qc.clear();
  }, [isDemoMode, qc]);

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    signIn,
    signOut,
    refresh,
  };
}
